import type { IAttribute, IHeadTag } from '@carboncopyco/types';
import * as DOMPurify from 'dompurify';
import camelCase from 'lodash-es/camelCase';
import parseInt from 'lodash-es/parseInt';
import { SVG_NAMESPACE_PREFIX, SVG_NAMESPACE_RESOLVER } from './constants';
import { getFileName } from './files';

export function injectScript(options: {
  src: string;
  id?: string;
  type?: string;
  defer?: boolean;
  async?: boolean;
}) {
  if (document.getElementById(options.id)) {
    return;
  }
  const script = document.createElement('script');
  script.setAttribute('src', options.src);
  script.setAttribute('type', options?.type || 'text/javascript');
  options.id && script.setAttribute('id', options.id);
  options.defer && script.setAttribute('defer', 'defer');
  options.async && script.setAttribute('async', 'async');

  return document.body.appendChild(script);
}

export const injectHeadTags = (headTags: IHeadTag[]): void => {
  headTags.forEach((tag) => {
    if (tag.name !== 'meta') {
      return;
    }
    const tagToInject = document.createElement(tag.name);

    tag.attributes.forEach((attribute) => {
      tagToInject.setAttribute(
        attribute.name,
        DOMPurify.sanitize(attribute.value)
      );
    });

    document.head.appendChild(tagToInject);
  });
};

const sanitizeAttributes = (attributes: IAttribute[]): IAttribute[] => {
  return attributes.map((attr) => ({
    name: DOMPurify.sanitize(attr.name),
    value: DOMPurify.sanitize(attr.value),
  }));
};

export const sanitizeTags = (tags: IHeadTag[]): IHeadTag[] => {
  return tags.map((tag) => {
    return {
      ...tag,
      attributes: sanitizeAttributes(tag.attributes),
    };
  });
};

interface ClientBoundaries {
  clientHeight: number;
  clientWidth: number;
}

interface PseudoElements {
  after: CSSStyleDeclaration;
  before: CSSStyleDeclaration;
}

export function preventDefaults(e: Event): void {
  stopPropagation(e);
  e.preventDefault();
}

export function stopPropagation(e: Event): void {
  e.stopPropagation();
  e.stopImmediatePropagation();
}

export function isNodeElement(node: HTMLElement): boolean {
  return node.nodeType === Node.ELEMENT_NODE;
}

export function calculateZIndex(): number {
  // Calculate the z-index and consider edit tool elements if they exists (RND-3644).
  const maxZIndex = getMaxZIndex();
  const tooleEl = getElementByAttribute('data-edit-tool');

  if (tooleEl) {
    return Number.parseFloat(window.getComputedStyle(tooleEl).zIndex);
  }

  return maxZIndex + 1;
}

export function getMaxZIndex(): number {
  let maxZIndex = Math.max(
    ...Array.from(document.querySelectorAll('body *'))
      .map((a) => Number.parseFloat(window.getComputedStyle(a).zIndex))
      .filter((a) => !Number.isNaN(a))
  );

  if (maxZIndex === Number.POSITIVE_INFINITY || maxZIndex <= 0) {
    maxZIndex = 1000;
  }

  return maxZIndex;
}

export function getUrlBackgroundImage(
  target: Element,
  computedStyle = true
): string {
  const regexForGettingMultipleUrls = /url\((.*?)\)/gi;
  const regexForGettingUrlValue = /url\(|\)$|"/gi;
  const regexForValidatingUrl =
    /(?:(?:http?|ftp)[s]*:\/\/|(\/|.\/|..\/))([^\s(["<,>/]*)(\/)[^\s[",><]*(.jpg|.jpeg|.png|.gif|.wbep|.svg)(\?[^\s[",><]*)?/gi;
  const backgroundStyle = computedStyle
    ? window.getComputedStyle(target).backgroundImage
    : (target as HTMLElement).style.backgroundImage;

  const isValidUrl = backgroundStyle.match(regexForValidatingUrl);
  const urls = backgroundStyle.match(regexForGettingMultipleUrls);
  const url = isValidUrl
    ? backgroundStyle.replace(regexForGettingUrlValue, '')
    : '';

  return urls?.length
    ? urls.map((url) => url.replace(regexForGettingUrlValue, '')).join(',')
    : url;
}

export function hasUrlBackgroundImage(
  target: Element,
  computedStyle = true
): boolean {
  const srcStr = getUrlBackgroundImage(target, computedStyle);
  return srcStr !== 'none' && srcStr !== 'initial' && srcStr !== '';
}

export const getBase64Image = (img: HTMLImageElement) => {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
};

export const loadUrlToImage = async (
  url: string,
  options?: { crossOrigin?: string }
): Promise<HTMLImageElement> => {
  const image = document.createElement('img');
  image.src = url;
  image.crossOrigin = options?.crossOrigin;

  return new Promise((res, rej) => {
    image.onload = () => {
      res(image);
    };
    image.onerror = rej;
  });
};

export function getPseudoElement(target: HTMLElement): PseudoElements {
  const before = window.getComputedStyle(target, ':before');
  const after = window.getComputedStyle(target, ':after');
  return {
    before: before.getPropertyValue('content') !== 'none' && before,
    after: after.getPropertyValue('content') !== 'none' && after,
  };
}

export function isSingleImageChild(target: HTMLElement): boolean {
  const children = Array.from(target.children);
  //If the target has just one child and it's an img tag.
  return children[0] && children[0].localName === 'img'
    ? !children.filter((child: HTMLElement) => child.localName !== 'img').length
    : false;
}

export function hasPseudoElementsOnImage(target: HTMLElement): boolean {
  const { before, after } = getPseudoElement(target);

  return (before || after) && isSingleImageChild(target);
}

export function toggleClass(
  target: HTMLElement,
  css: string | string[],
  remove?: boolean
): void {
  const style = typeof css === 'string' ? [css] : css;
  const method = remove ? 'remove' : 'add';

  target.classList[method](...style);

  if (!target.classList.length) {
    target.removeAttribute('class');
  }
}

export function toggleFocus(
  target,
  style: string | string[],
  remove?: boolean
): void {
  toggleClass(target, style, remove);
  remove ? target.blur() : target.focus();
}

export function isHtmlElement(element: Element): boolean {
  return element instanceof HTMLElement;
}

export function setAttribute(target: HTMLElement, attr): void {
  target?.setAttribute(attr, 'true');
}

export function setStyleAttribute(target: HTMLElement, attr): void {
  target?.setAttribute('style', attr);
}

export function removeAttribute(target: HTMLElement, attr): void {
  target?.removeAttribute(attr);
}

export function getElementByAttribute(
  attr: string,
  documentRoot: Document = document
): HTMLElement {
  return documentRoot.body?.querySelector(`[${attr}]`) as HTMLElement;
}

export function div(): HTMLDivElement {
  return document.createElement('div');
}

export function getClientBoundaries(): ClientBoundaries {
  return {
    clientHeight: window.innerHeight || document.documentElement.clientHeight,
    clientWidth: window.innerWidth || document.documentElement.clientWidth,
  };
}

export function calculateElementPadding(
  element: HTMLElement
): Record<string, number> {
  const paddings = [
    'padding',
    'padding-top',
    'padding-bottom',
    'padding-left',
    'padding-right',
  ].reduce((prev, curr) => {
    prev[camelCase(curr)] = Number.parseFloat(
      window.getComputedStyle(element, null).getPropertyValue(curr)
    );
    return prev;
  }, {});

  return paddings;
}

export function calculateElementNaturalDimensions(element: HTMLElement): {
  width: number;
  height: number;
} {
  const cs = getComputedStyle(element);

  const paddingX =
    (Number.parseFloat(cs.paddingLeft) || 0) +
    (Number.parseFloat(cs.paddingRight) || 0);
  const paddingY =
    (Number.parseFloat(cs.paddingTop) || 0) +
    (Number.parseFloat(cs.paddingBottom) || 0);

  const borderX =
    (Number.parseFloat(cs.borderLeftWidth) || 0) +
    (Number.parseFloat(cs.borderRightWidth) || 0);
  const borderY =
    (Number.parseFloat(cs.borderTopWidth) || 0) +
    (Number.parseFloat(cs.borderBottomWidth) || 0);

  // Element width and height minus padding and border
  const width = (element.offsetWidth || 0) - paddingX - borderX;
  const height = (element.offsetHeight || 0) - paddingY - borderY;

  return { width, height };
}

export const getParentFromChildXpath = (xpath: string): HTMLElement => {
  const xpathParts = xpath.split('/');
  const parentXPathParts = xpathParts.slice(0, -1);
  const parentXPath = parentXPathParts.join('/');

  return lookupElementByXPath(parentXPath);
};

export const getIndexFromXPath = (xpath: string): number => {
  const xpathParts = xpath.split('/');
  const lastPart = xpathParts[xpathParts.length - 1];
  const match = lastPart.match(/\d+/);
  return Number(match ? match[0] : 1); // gets the number between the square brackets
};

export const addStyleSheet = (id: string, content: string): void => {
  const styleSheet = document.createElement('style');

  styleSheet.id = id;
  styleSheet.innerHTML = content;
  document.head.append(styleSheet);
};

export const removeStyleSheet = (id: string): void => {
  const style = document.head.querySelector(`#${id}`);

  if (style) {
    style.remove();
  }
};

// Remove animation - don't delete - maybe will be used later
// export function removeAnimations(remove?: boolean) {
//   const style = document.createElement("style")
//   style.innerHTML =
//     "* {" +
//     "/*CSS transitions*/" +
//     " -o-transition-property: none !important;" +
//     " -moz-transition-property: none !important;" +
//     " -ms-transition-property: none !important;" +
//     " -webkit-transition-property: none !important;" +
//     "  transition-property: none !important;" +
//     "  /*CSS animations*/" +
//     "   -webkit-animation: none !important;" +
//     "   -moz-animation: none !important;" +
//     "   -o-animation: none !important;" +
//     "   -ms-animation: none !important;" +
//     "   animation: none !important;}"
//   document.getElementsByTagName("head")[0].appendChild(style)
// }

export function getElementFromSerializedXml(
  xmlString: string
): HTMLElement | null {
  const parserErrorSelector = 'parsererror';
  const parsedElement = new DOMParser().parseFromString(
    xmlString,
    'application/xml'
  ).children[0] as HTMLElement;
  const parserError = parsedElement.querySelector(parserErrorSelector);

  return parserError ? null : parsedElement;
}

export function createXPathFromElement(
  elm: HTMLElement | SVGElement
): string | null {
  let segments;
  for (
    segments = [];
    elm && elm.nodeType === Node.ELEMENT_NODE;
    elm = elm.parentNode as HTMLElement
  ) {
    let i;
    let sibling;
    for (
      i = 1, sibling = elm.previousSibling;
      sibling;
      sibling = sibling.previousSibling
    ) {
      if (sibling.localName === elm.localName) {
        i++;
      }
    }
    const namespacePrefix =
      (elm as SVGElement).ownerSVGElement || elm.tagName.toLowerCase() === 'svg'
        ? SVG_NAMESPACE_PREFIX
        : '';
    segments.unshift(`${namespacePrefix}${elm.localName.toLowerCase()}[${i}]`);
  }

  return segments.length ? `/${segments.join('/')}` : null;
}

// takes an outer and an inner element and returns the relative xpath between them without the trailing slash.
// if they are the same xpath, return empty string, if it cannot find the relation between them, returns null.
export function createRelativeXpath(
  outerElement: HTMLElement,
  innerElement: HTMLElement
): string | null {
  //calculate the xpath from element1 to element2 relatively (element 2 is the deeper element)
  const outerElementXpath = createXPathFromElement(outerElement);
  const innerElementXpath = createXPathFromElement(innerElement);
  if (outerElementXpath && innerElementXpath) {
    if (outerElementXpath === innerElementXpath) {
      return '';
    }
    if (innerElementXpath.includes(outerElementXpath)) {
      let relativeXpath = innerElementXpath.replace(outerElementXpath, '');
      if (relativeXpath.substring(0, 1) === '/') {
        //remove trailing /
        relativeXpath = relativeXpath.substring(1);
      }
      return relativeXpath;
    }
  }
  return null;
}

// for xpath - "/div[1]" or "div[1]" or "div" will return 1
export function getElementXpathLevel(xpath = ''): number {
  const levelRegex = new RegExp(/\[(.*?)\]/);
  const tagWithLevel = xpath.split('/').pop();

  return levelRegex.test(tagWithLevel)
    ? Number(levelRegex.exec(tagWithLevel)[1])
    : 1;
}

export const SHADOW_ROOT_XPATH_SEPARATOR = '/+';

export function lookupElementByXPath(
  xpath: string,
  fromElement?: HTMLElement,
  documentRoot: Document = document
): HTMLElement | null {
  if (!xpath) {
    return null;
  }

  if (xpath.includes(SHADOW_ROOT_XPATH_SEPARATOR)) {
    return getEditElementFromShadowRoot(xpath, documentRoot);
  }

  try {
    return documentRoot.evaluate(
      xpath,
      fromElement ? fromElement : documentRoot,
      (prefix) => {
        if (prefix === 'svg') {
          return SVG_NAMESPACE_RESOLVER;
        }
      },
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    )?.singleNodeValue as HTMLElement;
  } catch (e) {
    console.error(e);
    return undefined;
  }
}

export function getEditElementFromShadowRoot(
  xpath: string,
  documentRoot: Document = document
): HTMLElement {
  const xpaths = xpath.split(SHADOW_ROOT_XPATH_SEPARATOR);
  const hostElementXpath = xpaths.shift();
  let element = lookupElementByXPath(hostElementXpath, undefined, documentRoot);
  const shadowRootChildren = element?.shadowRoot?.children || element?.children;

  if (!shadowRootChildren) {
    return element;
  }

  let rootChildren = Array.from(shadowRootChildren);

  xpaths.forEach((xpath: string) => {
    const xpathTags = xpath.split('/').filter((str) => str !== '');

    xpathTags.forEach((tag, index) => {
      const tagParts = tag.split('[');
      const localName = tagParts[0];
      let elementIndex = Number(tagParts[1].replace(']', '')) - 1;
      const childrenElements = rootChildren.filter(
        (child) => child.localName === localName
      ) as HTMLElement[];
      const isLastTag = xpathTags.length - 1 === index;

      if (elementIndex < 0) {
        console.debug('shadow root element index < 0');
        elementIndex = 0;
      }

      if (!isLastTag) {
        if (childrenElements[elementIndex]?.children) {
          rootChildren = Array.from(
            childrenElements[elementIndex]?.shadowRoot?.children ||
              childrenElements[elementIndex].children
          );
        }
      } else {
        element = childrenElements?.[elementIndex];
        if (element?.shadowRoot?.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
          rootChildren = Array.from(element?.shadowRoot?.children);
        }
      }
    });
  });

  return element;
}

export function getShadowRootHostElement(xpath = ''): HTMLElement | null {
  if (!xpath.includes(SHADOW_ROOT_XPATH_SEPARATOR)) {
    return lookupElementByXPath(xpath);
  }

  const hostElementXpath = xpath.split(SHADOW_ROOT_XPATH_SEPARATOR)[0];

  return lookupElementByXPath(hostElementXpath);
}

export function getImageParams(
  target: HTMLImageElement | HTMLElement,
  computedStyle = true
): HTMLImageElement {
  if (hasUrlBackgroundImage(target, computedStyle)) {
    const img = new Image();
    img.src = getUrlBackgroundImage(target, computedStyle);

    return img;
  }

  return target as HTMLImageElement;
}

export function serializeDOMElement(elem: HTMLElement): string {
  const str = new XMLSerializer().serializeToString(elem);
  return str;
}

export function deSerializeDOMElement(serializedEl: string): HTMLElement {
  const parser = new DOMParser();
  const elem = parser.parseFromString(serializedEl, 'text/html').body
    .firstChild as HTMLElement;

  return elem;
}

export function getImageExtension(path = ''): string {
  const imageExtensionsList = [
    'JPEG',
    'JPG',
    'PNG',
    'GIF',
    'TIFF',
    'SVG',
    'WEBP',
  ];
  let fileExtension = '';

  if (path.startsWith('data:image/')) {
    fileExtension = path
      .substring('data:image/'.length, path.indexOf(';base64'))
      ?.toUpperCase();
  } else {
    const fileName = getFileName(path);
    const dotPosition = fileName.lastIndexOf('.');

    if (fileName && dotPosition > 0) {
      fileExtension = imageExtensionsList.filter((extension) =>
        fileName
          .slice(dotPosition + 1)
          .toLowerCase()
          .includes(extension.toLowerCase())
      )[0]; // extract extension ignoring `.`
    }
  }

  return fileExtension;
}

export function getElementFromXpath(
  documentRoot: Document,
  xpath: string
): HTMLElement {
  if (!xpath) {
    return documentRoot.body;
  }
  const targetEl = documentRoot.evaluate(
    xpath,
    documentRoot,
    (prefix) => {
      if (prefix === 'svg') {
        return SVG_NAMESPACE_RESOLVER;
      }
    },
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null
  ).singleNodeValue as HTMLElement;
  if (targetEl) {
    return targetEl;
  }
  const newXpath = xpath.split('/');
  newXpath.pop();
  return getElementFromXpath(documentRoot, newXpath.join('/'));
}

export const createEmbeddedIframeCode = (link: string, title?: string) => {
  return `<iframe width="100%" height="100%" src="${link}" title="${title}" frameBorder="0"></iframe>`;
};

export function isReactElement<T extends HTMLElement>(
  el: T
): {
  propsKey: string;
  fiberKey: string;
  reactEventHandler: string;
  reactInternalInstance: string;
} | null {
  // searching for react attributes on the element
  const reactFibersKey = Object.keys(el).find((key) =>
    /__reactFiber/.test(key)
  );
  const reactPropsKey = Object.keys(el).find((key) => /__reactProps/.test(key));
  const reactEventHandlersKey = Object.keys(el).find((key) =>
    /__reactEventHandlers/.test(key)
  );
  const reactInternalInstanceKey = Object.keys(el).find((key) =>
    /__reactInternalInstance/.test(key)
  );

  if (
    (!reactFibersKey || !reactPropsKey) &&
    (!reactEventHandlersKey || !reactInternalInstanceKey)
  ) {
    return null;
  }

  // return the key string in order to modify it on the element (auto-generated key).
  return {
    propsKey: reactPropsKey,
    fiberKey: reactFibersKey,
    reactEventHandler: reactEventHandlersKey,
    reactInternalInstance: reactInternalInstanceKey,
  };
}

export const parseStringToElement = (xml: string): Element | null => {
  const strategies = [
    (xml) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, 'text/html');
      return doc.body.firstElementChild;
    },
    (xml) => {
      const container = document.createElement('div');
      const range = document.createRange();
      const fragment = range.createContextualFragment(xml);
      container.appendChild(fragment);

      return container.firstElementChild;
    },
  ];

  for (const strategy of strategies) {
    const element = strategy(xml);
    if (element) {
      return element;
    }
  }

  return null;
};

export const isMovementAllowed = (
  target: HTMLElement
): {
  hasNextSibling: boolean;
  hasPreviousSibling: boolean;
  disabledDueToCSS: boolean;
} => {
  // this checks if the element has siblings and if it has position absolute/fixed and returns booleans for each

  const hasRelevantPositionAndSize = (el: HTMLElement): boolean => {
    if (!el) {
      return false;
    }
    const { position, width, height, display, visibility } =
      getComputedStyle(el);
    if (position === 'absolute' || position === 'fixed') {
      return false;
    }
    if (parseInt(width) === 0 || parseInt(height) === 0) {
      return false;
    }
    if (display === 'none') {
      return false;
    }
    if (visibility === 'hidden') {
      return false;
    }
    return true;
  };
  const hasNextSibling =
    !!target.nextElementSibling &&
    hasRelevantPositionAndSize(target.nextElementSibling as HTMLElement);
  const hasPreviousSibling =
    !!target.previousElementSibling &&
    hasRelevantPositionAndSize(target.previousElementSibling as HTMLElement);

  const disabledDueToCSS = !hasRelevantPositionAndSize(target);
  return {
    hasNextSibling,
    hasPreviousSibling,
    disabledDueToCSS,
  };
};

export const DS_DRAGGING_BOUNDRY_ID = 'ds-dragging-boundry';

export const getRootDocumentFromElementXpath = (
  xpath: string,
  parent: Document = document
): {
  documentRoot: Document;
  xpathFromRootDocument: string;
} => {
  const targetElement = getElementFromXpath(parent, xpath);
  if (targetElement.tagName === 'IFRAME') {
    return {
      documentRoot: (targetElement as HTMLIFrameElement).contentDocument,
      xpathFromRootDocument: xpath.split('iframe[1]')[1],
    };
  }
  return {
    documentRoot: parent,
    xpathFromRootDocument: xpath,
  };
};
